import React, { useEffect, useState } from "react";
import { get } from "lodash";
import {
  Page,
  PendingButton,
  required,
  SubmitButton,
  FormGroup,
} from "components/common";
import { Control, actions, Errors } from "react-redux-form";
import { Trans } from "react-i18next";
import { Button, Alert } from "reactstrap";
import { useForm } from "hooks";
import { isEmail } from "validator";
import { NotificationManager } from "react-notifications";
import { useClient, useUserContext } from "../../../hooks";
import { useHistory } from "react-router-dom";

const errors = {
  InviteNotFound: "",
  InviteExpired: "",
  EmployeeAlreadyLinked: "",
  InviteAlreadyAccepted: "",
  EmployeeInviteAlreadyLinked: ""
};

const ErrorAlert = ({ children }) => <Alert color="danger">{children}</Alert>;

const LOADING = "LOADING";
const ERROR = "ERROR";
const LOGIN = "LOGIN";
const ACCEPT = "ACCEPT";
const REGISTER = "REGISTER";
const SUCCESS = "SUCCESS";
const PAYROLL = "payroll";
const EINVOICE = "e-invoice";

export const Invite = ({ match, t }) => {
  const { token, invitation_type } = match.params;
  const [state, setState] = useState(LOADING);
  const [invite, setInvite] = useState();
  const [error, setError] = useState();
  const [pending, setPending] = useState();
  const history = useHistory();
  const [, setUser] = useState();
  const { Form: LoginForm, dispatch: loginDispatch } = useForm();
  const { Form: RegisterForm, dispatch: registerDispatch } = useForm({
    enableStoreLogging: true,
  });
  const { query } = useUserContext();
  const client = useClient();

  // const pageRefresh = () => {
  //   window.location.reload();
  // }

  async function onLogin({ email, password }) {
    try {
      await client.post("/auth", {
        email:email.toLowerCase(),
        password,
      });
      await query.refetch({ throwOnError: true });
      setState(ACCEPT);
    } catch (ex) {
      const status = get(ex, "response.status");

      if (status === 400) {
        NotificationManager.error(t("Invalid username or password"));
      } else {
        NotificationManager.error(t("Unable to log in at this time"));
      }
    } finally {
      loginDispatch(actions.setPending("local", false));
    }
  }

  async function onRegister(data) {
    registerDispatch(actions.resetErrors("errors"));

    try {
      await client.post("register/employee", {...data, invite_id:invite.invite_id});
      await onLogin(data);
    } catch (ex) {
      const data = get(ex, "response.data") || {};

      if (data) {
        registerDispatch(
          actions.setErrors(
            "errors.email",
            data.email ? "A user with this email address already exists" : false
          )
        );
        registerDispatch(actions.setErrors("errors.password", data.password));
      }
    } finally {
      registerDispatch(actions.setPending("local", false));
    }
  }

  function reset() {
    setState(null);
    setInvite(null);
    setError(null);
    setPending(null);
    setUser(null);
  }

  async function onAcceptInvite() {
    setPending(true);
    setError(null);

    try {
      await client.post("invites", {
        token,
      });
      setState(SUCCESS);
      
      setTimeout(() => {
        window.location.reload();
      },2000);
      
      setTimeout(()=>{
        history.push('/');
      },1000);

    } catch (ex) {
      setInviteError(ex);
    } finally {
      setPending(false);
    }
  }

  function setInviteError(ex) {
    const code = get(ex, "response.data.code");
    const error = errors[code];

    if (typeof error === "undefined") throw ex;
    setState(ERROR);
    setError(t(error === "" ? ex.response.data.detail : error));
  }

  useEffect(
    () =>
      void (async () => {
        reset();

        const user = await client.get("me").catch((err) => null);
        setUser(user);

        let data = null;
        try {
          data = await client
            .post("invites/check", {
              token,
            })
            .get("data");
          setInvite(data);

          registerDispatch(actions.change("local", data.employee));
          loginDispatch(
            actions.change("local", {
              email: data.employee.email,
              password: "",
            })
          );
          setState(user ? ACCEPT : LOGIN);
        } catch (ex) {
          setInviteError(ex);
        }
      })(),
    [token]
  );

  if (!state) return null;

  return (
    <Page title="Invitation" className="text-center mt-3">
      {state === ERROR && <div className="text-center">{error}</div>}
      {(state === ACCEPT ||
        state === REGISTER ||
        state === LOGIN ||
        state === SUCCESS) && (
        <>
          <Trans>
            <p>
              You've been invited to join{" "}
              <strong>{{ organisation: invite.organisation }}</strong>
              's {{invitation_type}}.
            </p>
            <p>
              Once joined, you'll be able to access your
              {invitation_type === PAYROLL ? ' payslips and time-off' : ` ${EINVOICE}`}.
            </p>
          </Trans>
          {state === ACCEPT && (
            <PendingButton
              color="primary"
              onClick={onAcceptInvite}
              pending={pending}
            >
              Accept Invite
            </PendingButton>
          )}
          {state === LOGIN && (
            <div>
              <hr />
              <h5 className="mb-3">
                <Trans>Log in to your Hesaab account</Trans>
              </h5>
              <div className="d-flex justify-content-center text-left">
                <LoginForm
                  onSubmit={onLogin}
                  validators={{
                    email: { required, isEmail },
                    password: { required },
                  }}
                >
                  <FormGroup
                    label="Email Address"
                    model=".email"
                    style={{ width: "20rem" }}
                  >
                    <Control.text model=".email" />
                  </FormGroup>
                  <FormGroup label="Password" model=".password">
                    <Control.text type="password" model=".password" />
                  </FormGroup>
                  <div className="d-flex justify-content-between">
                    <Button color="link" onClick={() => setState(REGISTER)}>
                      <Trans>Register</Trans>
                    </Button>
                    <SubmitButton>Login</SubmitButton>
                  </div>
                </LoginForm>
              </div>
            </div>
          )}
          {state === SUCCESS && (
            <div>
              <hr />
              <Alert color="success">
                <Trans>
                  Your account has been successfully linked to{" "}
                  <strong>{{ organisation: invite.organisation }}</strong>
                  's {invitation_type === PAYROLL ? PAYROLL : EINVOICE}.
                </Trans>
              </Alert>
              {/* <Redirect to={invitation_type === 'e-invoice' ? "/employee_einvoice" : "/employee_payroll"} /> */}
            </div>
            
          )}
          {state === REGISTER && (
            <div>
              <hr />
              <h5 className="mb-3">
                <Trans>Register for a Hesaab account</Trans>
              </h5>
              <div className="text-left d-flex justify-content-center">
                <div style={{ width: "25rem" }}>
                  <RegisterForm
                    onSubmit={onRegister}
                    validators={{
                      first_name: { required },
                      last_name: { required },
                      email: { required, isEmail },
                      password: { required },
                      confirm_password: { required },
                      "": {
                        passwordsMatch: (v) =>
                          v.password === v.confirm_password,
                      },
                    }}
                  >
                    <FormGroup label="First Name *" model=".first_name">
                      <Control.text model=".first_name" updateOn="blur" />
                    </FormGroup>
                    <FormGroup label="Last Name *" model=".last_name">
                      <Control.text model=".last_name" updateOn="blur" />
                    </FormGroup>
                    <FormGroup label="Email Address *" model=".email">
                      <Control.text model=".email" updateOn="blur" />
                    </FormGroup>
                    <Errors model="errors.email" component={ErrorAlert} />
                    <FormGroup label="Password *" model=".password">
                      <Control.text
                        type="password"
                        model=".password"
                        updateOn="blur"
                      />
                    </FormGroup>
                    <FormGroup
                      label="Confirm Password *"
                      model=".confirm_password"
                    >
                      <Control.text type="password" model=".confirm_password" />
                    </FormGroup>
                    <Errors model="errors.password" component={ErrorAlert} />
                    <div className="d-flex justify-content-between">
                      <Button color="link" onClick={() => setState(LOGIN)}>
                        <Trans>Back</Trans>
                      </Button>
                      <SubmitButton>Register</SubmitButton>
                    </div>
                  </RegisterForm>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </Page>
  );
};

export default Invite;
